/**
 * Staun Product Tiers
 * Bridges the bundle tier radio selector with Dawn's cart.
 *
 * Selecting a tier:
 *   1. Updates the visible quantity input (so the spinner reflects the
 *      selected tier even when auto-add is OFF).
 *   2. If `data-staun-auto-add="true"`, POSTs directly to /cart/add.js
 *      with an explicit `quantity` field — NOT via Dawn's <product-form>
 *      submit flow. This is intentional: Dawn builds FormData(form) and
 *      relies on form-attribute association to pull in the quantity
 *      input that lives outside the form. That association is fragile
 *      and was sending qty=1 regardless of what the visible input said.
 *      Posting directly guarantees the right qty reaches Shopify.
 *   3. After the cart add succeeds, hands the response off to Dawn's
 *      <cart-drawer> or <cart-notification> via renderContents() so the
 *      drawer slides open with the correct line item — same UX as
 *      Dawn's native ATC.
 *
 * Reverse sync: changing the visible qty input highlights the matching
 * tier but does NOT re-trigger add-to-cart.
 */

'use strict';

(() => {
  const init = (root = document) => {
    root.querySelectorAll('[data-staun-tiers]').forEach(setupTierGroup);
  };

  const setupTierGroup = (tierGroup) => {
    if (tierGroup.dataset.staunTiersReady === '1') return;
    tierGroup.dataset.staunTiersReady = '1';

    const sectionId = tierGroup.dataset.section;
    if (!sectionId) return;

    const autoAdd = tierGroup.dataset.staunAutoAdd === 'true';

    const radios = Array.from(
      tierGroup.querySelectorAll('input[type="radio"][data-tier-qty]')
    );
    if (!radios.length) return;

    const qtyInput = findQtyInput(sectionId);

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const qty = parseInt(radio.dataset.tierQty, 10);
        if (Number.isNaN(qty)) return;

        // Always update the visible qty input so the spinner stays in sync.
        const target = qtyInput || findQtyInput(sectionId);
        if (target) setInputValue(target, qty);

        if (autoAdd) {
          addToCartDirect(sectionId, qty);
        }
      });
    });

    if (qtyInput) {
      const syncRadioFromQty = () => {
        const qty = parseInt(qtyInput.value, 10);
        if (Number.isNaN(qty)) return;

        const sorted = radios
          .map((r) => ({ radio: r, qty: parseInt(r.dataset.tierQty, 10) }))
          .filter((t) => !Number.isNaN(t.qty))
          .sort((a, b) => b.qty - a.qty);

        const match = sorted.find((t) => qty >= t.qty);
        if (match && !match.radio.checked) {
          match.radio.checked = true;
        }
      };

      qtyInput.addEventListener('change', syncRadioFromQty);
      qtyInput.addEventListener('input', syncRadioFromQty);
    }
  };

  const setInputValue = (input, value) => {
    if (!input) return;
    input.value = String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const findQtyInput = (sectionId) => {
    return (
      document.querySelector(`#Quantity-${sectionId}`) ||
      document.querySelector(`input[name="quantity"][form="product-form-${sectionId}"]`) ||
      document.querySelector(`#MainProduct-${sectionId} input[name="quantity"]`) ||
      document.querySelector(`product-form[data-section-id="${sectionId}"] input[name="quantity"]`)
    );
  };

  const findProductForm = (sectionId) => {
    return (
      document.querySelector(`#product-form-${sectionId}`) ||
      document.querySelector(`product-form[data-section-id="${sectionId}"] form`) ||
      document.querySelector(`#MainProduct-${sectionId} form[action*="/cart/add"]`) ||
      document.querySelector(`#MainProduct-${sectionId} form`)
    );
  };

  /**
   * Add `qty` of the section's currently-selected variant directly via
   * /cart/add.js, then refresh Dawn's cart drawer with the response.
   */
  const addToCartDirect = (sectionId, qty) => {
    const form = findProductForm(sectionId);
    if (!form) {
      console.warn('[staun-tiers] No product form found for section', sectionId);
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) {
      console.warn('[staun-tiers] No variant id input found in form', form);
      return;
    }
    const variantId = variantInput.value;

    const submitBtn = form.querySelector(
      'button[type="submit"], button[name="add"], input[type="submit"]'
    );

    // Respect Dawn's sold-out / unavailable state.
    if (submitBtn) {
      const isDisabled =
        submitBtn.disabled ||
        submitBtn.getAttribute('aria-disabled') === 'true' ||
        submitBtn.hasAttribute('disabled');
      if (isDisabled) {
        console.info('[staun-tiers] ATC button disabled, skipping add');
        return;
      }
    }

    const cart =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');

    // Loading state on the ATC button — visual continuity with Dawn's flow.
    const spinner = submitBtn ? submitBtn.querySelector('.loading__spinner') : null;
    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    // Build FormData explicitly — DO NOT new FormData(form) because we
    // don't trust the form-attribute association on this template.
    const formData = new FormData();
    formData.append('form_type', 'product');
    formData.append('utf8', '✓');
    formData.append('id', variantId);
    formData.append('quantity', String(qty));

    // Forward any line-item properties Dawn would normally include
    // (gift-card recipient, custom properties, selling-plan, etc.).
    form
      .querySelectorAll(
        'input[name^="properties["], input[name="selling_plan"], select[name="selling_plan"]'
      )
      .forEach((el) => {
        if (el.name && (el.value || el.type === 'checkbox')) {
          if (el.type === 'checkbox' && !el.checked) return;
          formData.append(el.name, el.value);
        }
      });

    if (cart && typeof cart.getSectionsToRender === 'function') {
      const sectionIds = cart
        .getSectionsToRender()
        .map((s) => s.id)
        .join(',');
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    const cartAddUrl =
      (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          // Shopify returns status on errors (e.g. inventory)
          console.error('[staun-tiers] /cart/add error', response);
          alert(
            response.description ||
              response.message ||
              'Unable to add to cart right now.'
          );
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          cart.renderContents(response);
        } else {
          // No drawer/notification available — fall back to cart page.
          window.location.href =
            (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch((e) => {
        console.error('[staun-tiers] /cart/add network error', e);
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('loading');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
